import React from 'react';
import {
    Popover,
    Box,
    Stack,
    Typography,
    Divider,
    Button,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { Close, ShoppingCartCheckout, ShoppingCart, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetCart } from '../../../hooks/storefront/useGetCart';
import { formatMoney } from '../../../utils/money';

const MAX_PREVIEW = 5;

/**
 * Quick cart preview from the header. Mounts next to the cart icon
 * — pure preview UI, no mutations. Click "Checkout" or "View cart"
 * to leave the popover with the actual flow.
 */
const MiniCartPopover = ({ anchorEl, open, onClose }) => {
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const navigate = useNavigate();
    const { cartData: cart, isLoading } = useGetCart();

    const items = cart?.items || [];
    const previewItems = items.slice(0, MAX_PREVIEW);
    const overflowCount = items.length - previewItems.length;
    const subtotal = Number(cart?.subtotal) || 0;
    const currency = cart?.currency;

    const go = (path) => {
        onClose?.();
        navigate(path);
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: isArabic ? 'left' : 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: isArabic ? 'left' : 'right',
            }}
            slotProps={{
                paper: {
                    sx: {
                        width: 360,
                        maxWidth: 'calc(100vw - 24px)',
                        mt: 1,
                        borderRadius: 2,
                        overflow: 'hidden',
                    },
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText' }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <ShoppingCart fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={700}>
                        {t('cart.miniTitle', 'Your cart')}
                    </Typography>
                </Stack>
                <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
                    <Close fontSize="small" />
                </IconButton>
            </Stack>

            {isLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={20} />
                </Box>
            ) : items.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {t('cart.empty', 'Your cart is empty')}
                    </Typography>
                    <Button size="small" variant="contained" onClick={() => go('/')}>
                        {t('cart.continueShopping', 'Continue Shopping')}
                    </Button>
                </Box>
            ) : (
                <>
                    <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                        {previewItems.map((item, idx) => {
                            const name = item.product?.name?.[isArabic ? 'ar' : 'en']
                                || item.product?.name?.en
                                || item.product?.name
                                || '';
                            const lineTotal = Number(item.price || item.unit_price) * Number(item.qty);
                            return (
                                <React.Fragment key={item.id}>
                                    {idx > 0 && <Divider />}
                                    <Stack
                                        direction="row"
                                        spacing={1.25}
                                        alignItems="center"
                                        sx={{ p: 1.25 }}
                                    >
                                        {item.product?.thumb_url || item.product?.image ? (
                                            <Box
                                                component="img"
                                                src={item.product.thumb_url || item.product.image}
                                                alt=""
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 1,
                                                    objectFit: 'cover',
                                                    flexShrink: 0,
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 1,
                                                    bgcolor: 'action.hover',
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap>
                                                {name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('cart.qty', 'Qty')}: {item.qty}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" fontWeight={700}>
                                            {formatMoney(lineTotal, currency)}
                                        </Typography>
                                    </Stack>
                                </React.Fragment>
                            );
                        })}
                        {overflowCount > 0 && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', textAlign: 'center', py: 1 }}
                            >
                                + {overflowCount} {t('cart.moreItems', 'more')}
                            </Typography>
                        )}
                    </Box>

                    <Divider />

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ px: 2, py: 1.25 }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {t('cart.subtotal', 'Subtotal')}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                            {formatMoney(subtotal, currency)}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ px: 2, pb: 2 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            onClick={() => go('/cart')}
                            endIcon={<ArrowForward />}
                        >
                            {t('cart.viewCart', 'View cart')}
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => go('/checkout')}
                            startIcon={<ShoppingCartCheckout />}
                        >
                            {t('cart.checkout', 'Checkout')}
                        </Button>
                    </Stack>
                </>
            )}
        </Popover>
    );
};

export default MiniCartPopover;
